import React, { useState, useMemo } from 'react';
import RoleBasedSidebar from '../../components/ui/RoleBasedSidebar';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import OrderFilters from './components/OrderFilters';
import OrderStats from './components/OrderStats';
import OrderList from './components/OrderList';
import OrderModal from './components/OrderModal';

const OrderManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole] = useState('admin');
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'view',
    selectedOrder: null
  });

  const mockOrders = [
    {
      id: 1,
      folio: 'ORD-2026-001',
      customer: 'Juan Pérez',
      phone: '612-345-6789',
      vehicle: 'Toyota Corolla 2020',
      plates: 'ABC-123',
      status: 'en_proceso',
      technician: 'Miguel Sánchez',
      date: '2026-02-15',
      itemCount: 3,
      total: 285.00,
      discount: 0,
      items: [
        { id: 1, type: 'service', name: 'Cambio de aceite', quantity: 1, price: 45.00 },
        { id: 2, type: 'product', name: 'Llanta 185/65 R15', quantity: 2, price: 85.00 },
        { id: 3, type: 'service', name: 'Alineación', quantity: 1, price: 60.00 }
      ],
      notes: 'Cliente solicita revisión completa del sistema de frenos'
    },
    {
      id: 2,
      folio: 'ORD-2026-002',
      customer: 'María García',
      phone: '612-987-6543',
      vehicle: 'Honda Civic 2019',
      plates: 'XYZ-789',
      status: 'lista',
      technician: 'Roberto Díaz',
      date: '2026-02-14',
      itemCount: 2,
      total: 135.00,
      discount: 10.00,
      items: [
        { id: 1, type: 'service', name: 'Balanceo', quantity: 1, price: 40.00 },
        { id: 2, type: 'service', name: 'Revisión de frenos', quantity: 1, price: 80.00 }
      ],
      notes: ''
    },
    {
      id: 3,
      folio: 'ORD-2026-003',
      customer: 'Carlos López',
      phone: '612-456-7890',
      vehicle: 'Nissan Versa 2021',
      plates: 'DEF-456',
      status: 'cotizada',
      technician: 'Fernando Ruiz',
      date: '2026-02-15',
      itemCount: 4,
      total: 380.00,
      discount: 0,
      items: [
        { id: 1, type: 'product', name: 'Llanta 195/55 R16', quantity: 4, price: 95.00 }
      ],
      notes: 'Cotización para cambio completo de llantas'
    },
    {
      id: 4,
      folio: 'ORD-2026-004',
      customer: 'Ana Martínez',
      phone: '612-234-5678',
      vehicle: 'Mazda 3 2022',
      plates: 'GHI-012',
      status: 'aprobada',
      technician: 'Miguel Sánchez',
      date: '2026-02-13',
      itemCount: 3,
      total: 175.00,
      discount: 0,
      items: [
        { id: 1, type: 'service', name: 'Cambio de aceite', quantity: 1, price: 45.00 },
        { id: 2, type: 'product', name: 'Aceite 5W-30', quantity: 1, price: 35.00 },
        { id: 3, type: 'product', name: 'Filtro de aire', quantity: 1, price: 15.00 }
      ],
      notes: 'Mantenimiento preventivo programado'
    },
    {
      id: 5,
      folio: 'ORD-2026-005',
      customer: 'Roberto Hernández',
      phone: '612-567-8901',
      vehicle: 'Ford Focus 2018',
      plates: 'JKL-345',
      status: 'entregada',
      technician: 'Roberto Díaz',
      date: '2026-02-15',
      itemCount: 2,
      total: 100.00,
      discount: 5.00,
      items: [
        { id: 1, type: 'service', name: 'Alineación', quantity: 1, price: 60.00 },
        { id: 2, type: 'service', name: 'Balanceo', quantity: 1, price: 40.00 }
      ],
      notes: ''
    },
    {
      id: 6,
      folio: 'ORD-2026-006',
      customer: 'Laura Sánchez',
      phone: '612-678-9012',
      vehicle: 'Chevrolet Spark 2020',
      plates: 'MNO-678',
      status: 'cancelada',
      technician: 'Fernando Ruiz',
      date: '2026-02-12',
      itemCount: 1,
      total: 45.00,
      discount: 0,
      items: [
        { id: 1, type: 'service', name: 'Cambio de aceite', quantity: 1, price: 45.00 }
      ],
      notes: 'Cliente canceló por motivos personales'
    }
  ];

  const filteredOrders = useMemo(() => {
    return mockOrders?.filter(order => {
      const matchesSearch = !filters?.search || 
        order?.customer?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        order?.phone?.includes(filters?.search) ||
        order?.folio?.toLowerCase()?.includes(filters?.search?.toLowerCase());

      const matchesStatus = filters?.status === 'all' || order?.status === filters?.status;

      const matchesDateFrom = !filters?.dateFrom || order?.date >= filters?.dateFrom;
      const matchesDateTo = !filters?.dateTo || order?.date <= filters?.dateTo;

      return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [filters]);

  const stats = useMemo(() => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    return {
      total: mockOrders?.length,
      inProgress: mockOrders?.filter(o => o?.status === 'en_proceso')?.length,
      ready: mockOrders?.filter(o => o?.status === 'lista')?.length,
      deliveredToday: mockOrders?.filter(o => o?.status === 'entregada' && o?.date === today)?.length
    };
  }, []);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleCreateOrder = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
      selectedOrder: null
    });
  };

  const handleViewOrder = (order) => {
    setModalState({
      isOpen: true,
      mode: 'view',
      selectedOrder: order
    });
  };

  const handleEditOrder = (order) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      selectedOrder: order
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      mode: 'view',
      selectedOrder: null
    });
  };

  const handleSaveOrder = (orderData) => {
    console.log('Guardando orden:', orderData);
    handleCloseModal();
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        userRole={userRole}
      />
      <main
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-6">
          <NavigationBreadcrumb />

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Gestión de Órdenes
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Administra cotizaciones y órdenes de servicio
            </p>
          </div>

          <OrderStats stats={stats} />

          <OrderFilters
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            onCreateOrder={handleCreateOrder}
          />

          <OrderList
            orders={filteredOrders}
            onView={handleViewOrder}
            onEdit={handleEditOrder}
            onUpdateStatus={(order) => console.log('Actualizar estado:', order)}
            loading={loading}
          />
        </div>
      </main>
      <OrderModal
        order={modalState?.selectedOrder}
        isOpen={modalState?.isOpen}
        onClose={handleCloseModal}
        onSave={handleSaveOrder}
        mode={modalState?.mode}
        userRole={userRole}
      />
    </div>
  );
};

export default OrderManagement;